import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api.js";
import { MapPin, Wrench, Package, PaintBucket, ShieldCheck, MessageCircle, Loader2, TrendingUp } from "lucide-react";
import PageHeader from "../components/PageHeader.jsx";

/**
 * Mozambique dealer map — executive view with pins coloured by open-case load.
 *
 * Projection: linear equirectangular over the Mozambique bounding box
 * (lon 30→41, lat -10 → -27). Not geodesically accurate — but it's fine for
 * a 11-pin dashboard and avoids pulling in a map library.
 */
const BBOX = { lonMin: 30.0, lonMax: 41.0, latMin: -27.0, latMax: -10.3 };
const SVG_W = 480;
const SVG_H = 760;
// Extra padding on the right so east-coast branch labels (Beira, Quelimane,
// Nampula, Nacala, Pemba) fit inside the viewBox and aren't clipped.
const LABEL_PAD_RIGHT = 110;
const VIEW_W = SVG_W + LABEL_PAD_RIGHT;

function project(lat, lng) {
  const x = ((lng - BBOX.lonMin) / (BBOX.lonMax - BBOX.lonMin)) * SVG_W;
  const y = SVG_H - ((lat - BBOX.latMin) / (BBOX.latMax - BBOX.latMin)) * SVG_H;
  return [x, y];
}

const MZ_OUTLINE = [[-26.840,32.114],[-26.010,32.071],[-25.813,31.906],[-24.423,31.987],[-23.931,31.850],[-23.457,31.528],[-22.415,31.279],[-21.314,32.447],[-21.134,32.340],[-20.936,32.491],[-20.603,32.482],[-20.532,32.672],[-20.032,33.007],[-19.784,33.033],[-19.674,32.820],[-19.464,32.763],[-19.084,32.856],[-18.955,32.681],[-18.834,32.691],[-18.775,32.903],[-18.536,32.871],[-18.352,33.043],[-17.973,32.927],[-17.346,33.022],[-16.935,32.829],[-16.682,32.969],[-16.709,32.731],[-16.452,32.290],[-16.429,31.910],[-16.023,31.260],[-16.001,30.403],[-15.481,30.393],[-14.981,30.214],[-14.014,33.203],[-14.602,33.645],[-14.490,33.720],[-14.387,34.344],[-14.607,34.518],[-15.271,34.569],[-15.890,34.233],[-16.274,34.426],[-16.833,35.118],[-17.110,35.066],[-17.129,35.292],[-16.701,35.278],[-16.538,35.132],[-16.141,35.375],[-16.042,35.785],[-15.172,35.784],[-14.887,35.904],[-14.650,35.847],[-13.704,35.084],[-13.492,34.641],[-13.326,34.545],[-12.199,34.354],[-11.578,34.617],[-11.613,35.532],[-11.413,35.826],[-11.702,36.200],[-11.721,36.626],[-11.565,37.032],[-11.719,37.470],[-11.534,37.817],[-11.284,37.938],[-11.413,38.492],[-11.168,38.896],[-11.174,39.238],[-10.933,39.786],[-10.478,40.511],[-10.687,40.655],[-10.762,40.485],[-10.841,40.620],[-11.317,40.349],[-11.385,40.466],[-11.839,40.520],[-12.501,40.478],[-12.766,40.647],[-12.942,40.411],[-12.976,40.595],[-13.511,40.527],[-14.032,40.651],[-14.164,40.539],[-14.271,40.746],[-14.566,40.629],[-14.411,40.801],[-14.705,40.848],[-14.854,40.643],[-14.998,40.774],[-15.131,40.528],[-15.233,40.682],[-15.492,40.582],[-15.989,40.065],[-16.222,39.992],[-16.302,39.783],[-16.430,39.856],[-16.984,39.091],[-17.026,38.663],[-17.288,38.173],[-17.191,38.081],[-17.286,38.128],[-17.739,37.189],[-18.012,36.984],[-17.878,36.833],[-18.047,36.977],[-18.556,36.473],[-18.785,36.407],[-18.694,36.238],[-18.891,36.267],[-18.801,36.124],[-18.951,35.855],[-19.705,35.127],[-19.863,34.880],[-19.582,34.553],[-19.815,34.778],[-20.541,34.668],[-20.730,34.993],[-20.966,35.120],[-21.111,35.016],[-21.644,35.271],[-22.413,35.312],[-22.477,35.401],[-22.120,35.441],[-22.310,35.517],[-22.179,35.551],[-22.690,35.489],[-22.915,35.606],[-23.676,35.340],[-23.965,35.332],[-23.803,35.527],[-24.101,35.497],[-24.598,35.106],[-25.252,33.319],[-25.544,32.873],[-25.886,32.687],[-25.985,32.477],[-26.289,32.842],[-26.079,32.954],[-26.846,32.893],[-26.840,32.114]];

const MZ_PATH = (() => {
  const pts = MZ_OUTLINE.map(([lat, lng]) => project(lat, lng).join(","));
  return "M" + pts.join(" L") + " Z";
})();


function loadColor(total) {
  if (total === 0) return { pin: "#64748B", bg: "#F1F5F9", label: "sem casos" };
  if (total <= 2) return { pin: "#059669", bg: "#D1FAE5", label: "baixa" };
  if (total <= 5) return { pin: "#D97706", bg: "#FEF3C7", label: "média" };
  return { pin: "#DC2626", bg: "#FEE2E2", label: "alta" };
}


export default function BranchMapPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/branches/map").then((r) => setData(r.data));
  }, []);

  const sorted = useMemo(() => {
    if (!data?.branches) return [];
    return [...data.branches].sort((a, b) => b.total_open - a.total_open);
  }, [data]);

  const activeBranch = useMemo(() => {
    if (!data?.branches) return null;
    const name = selected || hovered;
    return data.branches.find((b) => b.name === name) || null;
  }, [data, hovered, selected]);

  if (!data) {
    return (
      <div className="p-10 flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> A carregar mapa…
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
      <PageHeader
        overline="Visão executiva"
        title="Mapa das filiais Ronil"
        subtitle="Distribuição operacional em tempo real — 12 dealerships, de Maputo a Afungi. Clique num pin para abrir o detalhe."
      />

      {/* Totals strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 mb-6">
        <TotalCard icon={Wrench}       label="SOMs abertas"    value={data.totals.open_cases} />
        <TotalCard icon={Package}      label="PCPs abertas"    value={data.totals.open_prs} />
        <TotalCard icon={PaintBucket}  label="Carroçaria"       value={data.totals.open_body_shop} />
        <TotalCard icon={ShieldCheck}  label="Garantias"        value={data.totals.open_warranty} />
        <TotalCard icon={MessageCircle} label="Reclamações"     value={data.totals.open_complaints} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6">
        {/* --- MAP --- */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 shadow-soft-card">
          <svg
            viewBox={`0 0 ${VIEW_W} ${SVG_H}`}
            width="100%"
            style={{ maxWidth: VIEW_W, height: "auto" }}
            className="block"
            data-testid="branch-map-svg"
          >
            {/* Ocean subtle grid */}
            <defs>
              <pattern id="dots" width="12" height="12" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="0.8" fill="#DCE4F0" />
              </pattern>
              <linearGradient id="landGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#EEF2F8" />
                <stop offset="1" stopColor="#DCE4F0" />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width={VIEW_W} height={SVG_H} fill="url(#dots)" opacity="0.4" />

            {/* Country shape */}
            <path
              d={MZ_PATH}
              fill="url(#landGradient)"
              stroke="#1F3864"
              strokeWidth="1.6"
              strokeLinejoin="round"
            />

            {/* City pins */}
            {data.branches.filter((b) => b.has_coords).map((b) => {
              const [x, y] = project(b.lat, b.lng);
              const c = loadColor(b.total_open);
              const isActive = (hovered || selected) === b.name;
              // Base radius is always the same so the hitbox doesn't move while
              // hovering. The active state grows the halo + colour emphasis only,
              // never shrinks the pin — avoiding mouse-enter/leave oscillation.
              const baseR = 7 + Math.min(6, b.total_open);
              const activeR = baseR + 3;
              const r = isActive ? activeR : baseR;
              // Label x position is anchored to baseR so it never jumps.
              const labelX = x + baseR + 4;
              return (
                <g
                  key={b.name}
                  onMouseEnter={() => setHovered(b.name)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => setSelected(b.name === selected ? null : b.name)}
                  style={{ cursor: "pointer" }}
                  data-testid={`map-pin-${b.name}`}
                >
                  {/* soft halo for load */}
                  <circle cx={x} cy={y} r={activeR + 6} fill={c.pin} opacity={isActive ? 0.22 : 0.12} />
                  <circle cx={x} cy={y} r={r} fill={c.pin} stroke="#FFFFFF" strokeWidth="2.5" style={{ transition: "r 120ms ease-out" }} />
                  {b.total_open > 0 && (
                    <text x={x} y={y + 3} fontSize="9" fontWeight="bold" fill="#FFFFFF" textAnchor="middle" style={{ pointerEvents: "none" }}>
                      {b.total_open}
                    </text>
                  )}
                  <text
                    x={labelX}
                    y={y + 3}
                    fontSize="10"
                    fontWeight="600"
                    fill={isActive ? "#1F3864" : "#334155"}
                    stroke="#FFFFFF"
                    strokeWidth="3"
                    style={{ pointerEvents: "none", fontFamily: "Chivo, sans-serif", paintOrder: "stroke" }}
                  >
                    {b.name}
                  </text>
                </g>
              );
            })}
          </svg>

          {/* Legend */}
          <div className="mt-3 pt-3 border-t border-slate-200 flex flex-wrap items-center gap-4 text-[11px]">
            <span className="text-[9px] uppercase tracking-widest text-slate-500 font-heading font-semibold">Carga</span>
            {[
              { color: "#64748B", label: "sem casos" },
              { color: "#059669", label: "1–2 baixa" },
              { color: "#D97706", label: "3–5 média" },
              { color: "#DC2626", label: "6+ alta" },
            ].map((l) => (
              <span key={l.label} className="inline-flex items-center gap-1 text-slate-600">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: l.color }} />
                {l.label}
              </span>
            ))}
          </div>
        </div>

        {/* --- Side details + ranking --- */}
        <div className="space-y-4">
          {activeBranch ? (
            <BranchDetail branch={activeBranch} onOpenCases={(b) => navigate(`/service-cases?branch=${encodeURIComponent(b)}`)} />
          ) : (
            <div className="bg-white border border-dashed border-slate-300 rounded-lg p-8 text-center text-sm text-slate-500 shadow-soft-card">
              <MapPin className="w-6 h-6 text-slate-400 mx-auto mb-2" />
              Passe o rato ou clique num pin para ver o detalhe da filial.
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-lg shadow-soft-card overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-ronil-navy" />
              <h3 className="font-heading font-semibold text-slate-900 text-sm">Ranking por carga aberta</h3>
            </div>
            <table className="w-full text-sm" data-testid="branch-ranking-table">
              <tbody>
                {sorted.map((b, i) => {
                  const c = loadColor(b.total_open);
                  return (
                    <tr
                      key={b.name}
                      onMouseEnter={() => setHovered(b.name)}
                      onMouseLeave={() => setHovered(null)}
                      onClick={() => setSelected(b.name === selected ? null : b.name)}
                      className={`border-b border-slate-100 last:border-0 cursor-pointer hover:bg-ronil-navy-50 ${selected === b.name ? "bg-ronil-navy-50" : ""}`}
                    >
                      <td className="pl-4 pr-2 py-2 text-slate-400 font-mono text-[11px] w-8">{String(i + 1).padStart(2, "0")}</td>
                      <td className="px-2 py-2 font-heading font-semibold text-slate-900">{b.name}</td>
                      <td className="px-2 py-2 text-center">
                        <span
                          className="inline-flex items-center gap-1 font-heading font-bold text-xs px-2 py-0.5 rounded-md"
                          style={{ background: c.bg, color: c.pin }}
                        >
                          {b.total_open}
                        </span>
                      </td>
                      <td className="pl-2 pr-4 py-2 text-right text-[11px] text-slate-500">
                        {b.open_cases > 0 && <span className="mr-2">{b.open_cases} SOM</span>}
                        {b.open_prs > 0 && <span>{b.open_prs} PCP</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function TotalCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 shadow-soft-card flex items-center gap-3">
      <div className="w-9 h-9 rounded-md bg-ronil-navy-50 flex items-center justify-center">
        <Icon className="w-4 h-4 text-ronil-navy" />
      </div>
      <div>
        <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-heading font-semibold">{label}</div>
        <div className="font-heading text-2xl font-bold text-ronil-navy tabular-nums leading-none mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function BranchDetail({ branch, onOpenCases }) {
  const c = loadColor(branch.total_open);
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-soft-card" data-testid={`branch-detail-${branch.name}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-ronil-sand font-heading font-bold">Filial</div>
          <h2 className="font-display text-2xl font-bold text-ronil-navy mt-0.5">{branch.name}</h2>
          <div className="text-[11px] text-slate-500 mt-1 font-mono">
            {branch.lat?.toFixed(3)}° S · {branch.lng?.toFixed(3)}° E
          </div>
        </div>
        <span
          className="inline-flex items-center gap-1.5 font-heading font-bold text-xs px-3 py-1 rounded-md"
          style={{ background: c.bg, color: c.pin }}
        >
          Carga {c.label}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <DetailRow icon={Wrench}       label="SOMs abertas"  value={branch.open_cases} />
        <DetailRow icon={Package}      label="PCPs abertas"  value={branch.open_prs} />
        <DetailRow icon={PaintBucket}  label="Carroçaria"    value={branch.open_body_shop} />
        <DetailRow icon={ShieldCheck}  label="Garantias"     value={branch.open_warranty} />
        <DetailRow icon={MessageCircle} label="Reclamações"  value={branch.open_complaints} />
      </div>

      {branch.latest_som_number && (
        <div className="mt-4 pt-4 border-t border-slate-100 text-[11px] text-slate-500">
          Último SOM criado: <span className="font-heading font-semibold text-ronil-navy">{branch.latest_som_number}</span>
        </div>
      )}

      <button
        onClick={() => onOpenCases(branch.name)}
        disabled={branch.open_cases === 0}
        className="mt-4 w-full bg-ronil-navy hover:bg-ronil-navy-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-md py-2 text-sm font-heading font-semibold transition-all shadow-sm hover:shadow-elevation"
        data-testid={`branch-open-cases-${branch.name}`}
      >
        Abrir SOMs de {branch.name}
      </button>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      <span className="text-slate-600 text-xs">{label}</span>
      <span className="ml-auto font-heading font-bold text-slate-900 tabular-nums">{value}</span>
    </div>
  );
}
